/**
 * utils/modelIcons.jsx
 *
 * Shared provider icon mapping for public chat.
 * Uses @lobehub/icons — same icons as PopUpSettings.jsx.
 *
 * Usage:
 *   import { ProviderIcon, getProviderFromModelKey } from "../utils/modelIcons";
 *   <ProviderIcon provider="openai" size={16} />
 *   <ProviderIcon provider={getProviderFromModelKey("openai/gpt-4o")} size={16} />
 */

import {
  OpenAI as OpenAIIcon,
  Anthropic as AnthropicIcon,
  Gemini as GeminiIcon,
  XAI as XAIIcon,
  Mistral as MistralIcon,
  DeepSeek as DeepSeekIcon,
  Cohere as CohereIcon,
  Azure as AzureIcon,
  AzureAI as AzureAIIcon,
  Bedrock as BedrockIcon,
  Aws as AwsIcon,
  GoogleCloud as GoogleCloudIcon,
  VertexAI as VertexAIIcon,
  Groq as GroqIcon,
  Fireworks as FireworksIcon,
  Together as TogetherAIIcon,
  DeepInfra as DeepInfraIcon,
  Replicate as ReplicateIcon,
  Perplexity as PerplexityIcon,
  Cerebras as CerebrasIcon,
  Ai21 as Ai21Icon,
  Cloudflare as CloudflareIcon,
  Ollama as OllamaIcon,
  HuggingFace as HuggingFaceIcon,
  OpenRouter as OpenRouterIcon,
  Vllm as VllmIcon,
  Anyscale as AnyscaleIcon,
  LmStudio as LMStudioIcon,
  NPLCloud as NPLCloudIcon,
} from "@lobehub/icons";

const PROVIDER_ICONS = {
  openai: OpenAIIcon,
  anthropic: AnthropicIcon,
  gemini: GeminiIcon,
  xai: XAIIcon,
  mistral: MistralIcon,
  deepseek: DeepSeekIcon,
  cohere: CohereIcon,
  azure: AzureIcon,
  azure_ai: AzureAIIcon,
  bedrock: BedrockIcon,
  sagemaker: AwsIcon,
  vertex_ai: VertexAIIcon,
  vertex_ai_beta: VertexAIIcon,
  groq: GroqIcon,
  fireworks_ai: FireworksIcon,
  together_ai: TogetherAIIcon,
  deepinfra: DeepInfraIcon,
  replicate: ReplicateIcon,
  perplexity: PerplexityIcon,
  cerebras: CerebrasIcon,
  ai21: Ai21Icon,
  cloudflare: CloudflareIcon,
  databricks: null,
  ollama: OllamaIcon,
  ollama_chat: OllamaIcon,
  huggingface: HuggingFaceIcon,
  openrouter: OpenRouterIcon,
  vllm: VllmIcon,
  anyscale: AnyscaleIcon,
  lm_studio: LMStudioIcon,
  nlp_cloud: NPLCloudIcon,
  "text-completion-openai": OpenAIIcon,
};

/**
 * Extract provider name from a model key.
 * "openai/gpt-4o" → "openai"
 * "gpt-4o" → "openai" (fallback)
 */
export function getProviderFromModelKey(modelKey) {
  if (!modelKey) return "";
  if (modelKey.includes("/")) return modelKey.split("/")[0];
  return "";
}

/**
 * Extract display name from a model key.
 * "openai/gpt-4o" → "gpt-4o"
 */
export function getModelDisplayName(modelKey) {
  if (!modelKey) return "";
  return modelKey.includes("/") ? modelKey.split("/").pop() : modelKey;
}

/**
 * ProviderIcon component — renders the color icon for a provider.
 *
 * @param {object} props
 * @param {string} props.provider - Provider key (e.g. "openai", "anthropic")
 * @param {number} [props.size=16] - Icon size in pixels
 * @param {string} [props.className] - Additional CSS classes
 */
export const ProviderIcon = ({ provider, size = 16, className = "" }) => {
  const Icon = PROVIDER_ICONS[provider];

  if (Icon) {
    return Icon.Color ? (
      <Icon.Color size={size} className={className} />
    ) : (
      <Icon size={size} className={className} />
    );
  }

  // Fallback: first letter in a small badge
  return (
    <span
      className={[
        "inline-flex items-center justify-center rounded-md bg-zinc-200 dark:bg-zinc-700 text-[10px] font-semibold text-zinc-600 dark:text-zinc-300 shrink-0",
        className,
      ].join(" ")}
      style={{ width: `${size}px`, height: `${size}px` }}
    >
      {(provider || "?").charAt(0).toUpperCase()}
    </span>
  );
};

export default ProviderIcon;
